/**
 * The columns of a fee-schedule category, as the negotiation stream addresses them.
 *
 * A fee schedule is negotiated one column at a time: `buildCollectionOperations`
 * (proposal-command-builder.ts) diffs a category leaf by leaf and emits a `CollectionItemUpdated` per
 * changed property, keyed `feeSchedule:categories:{itemId}:{property}`, whose payload is the property
 * wrapped back up on its own (`{ "feeRate": 125 }`). The server stores that verbatim
 * (ProposalCollectionValuePolicy's "expectedValue must use the same nested leaf shape as value"), so
 * the whole stack — conflicts, resolution targets, per-column history — is already per column.
 *
 * What was missing was a vocabulary for reading one back. `proposal-change-description.ts` renders a
 * stored value by looking at the value alone, and a bare `{ "feeRate": 125 }` is not a shape it can
 * name: only `name` happened to be a key it probed for, so every other column's history, save
 * confirmation and submission summary showed the column's label with nothing after it. This module is
 * that vocabulary — which columns exist, what each is called, how to pull one out of a leaf or whole
 * category payload, and how each reads — and it is deliberately the only place those six properties
 * are spelled, so the conflict key, the label, the reading order and the renderer cannot drift apart.
 */

import {
  proposalCollectionItemAspectLabel,
  proposalCollectionItemUnreadableValueText,
} from "work/proposals/negotiation/proposal-collection-item-revert";

/** One column of a fee-schedule category, named by its canonical property. */
export type ProposalFeeScheduleAspect =
  | "name"
  | "description"
  | "type"
  | "feeRate"
  | "feeCurrency"
  | "feeUnit";

/**
 * The order a category reads in: what it is called, what it covers, whether it is a fee or an
 * expense, then what it costs. Also the order the history, the save confirmation and the submission summary list the columns
 * in, so the schedule reads the same wherever the user meets it.
 */
export const proposalFeeScheduleAspects: readonly ProposalFeeScheduleAspect[] = [
  "name",
  "description",
  "type",
  "feeRate",
  "feeCurrency",
  "feeUnit",
];

/** What each column is called. The one spelling; every surface reads it from here. */
export const proposalFeeScheduleAspectLabels: Readonly<Record<ProposalFeeScheduleAspect, string>> = {
  name: "Name",
  description: "Description",
  type: "Type",
  feeRate: "Rate",
  feeCurrency: "Currency",
  feeUnit: "Unit",
};

/**
 * The columns a fee-schedule category offers a history of, in reading order — what
 * `ProposalFieldTimelineDropdown`'s `collectionItemAspects` takes (#4847). Derived from the two
 * declarations above rather than spelled again, so a column can never appear in the history menu under
 * a different name from the one the change summary gives it.
 */
export const proposalFeeScheduleAspectOptions: readonly { key: ProposalFeeScheduleAspect; label: string }[] =
  proposalFeeScheduleAspects.map((aspect) => ({ key: aspect, label: proposalFeeScheduleAspectLabels[aspect] }));

/** A category's columns, as far as a payload carries them. `null` is a column explicitly cleared. */
export type ProposalFeeScheduleCategoryValues = Partial<Record<ProposalFeeScheduleAspect, unknown>>;

const aspectSet = new Set<string>(proposalFeeScheduleAspects);

/** Whether `property` names a column of a fee-schedule category. */
export function isProposalFeeScheduleAspect(property: unknown): property is ProposalFeeScheduleAspect {
  return typeof property === "string" && aspectSet.has(property);
}

/**
 * The column a conflict key addresses, if it addresses one.
 *
 * `feeSchedule:categories:{itemId}` is the category itself — an add or a removal — and answers
 * undefined; `feeSchedule:categories:{itemId}:feeRate` is its rate.
 */
export function proposalFeeScheduleAspectFromConflictKey(
  conflictKey?: string | null,
): ProposalFeeScheduleAspect | undefined {
  if (!conflictKey) return undefined;
  const property = conflictKey.split(":").at(-1);
  return isProposalFeeScheduleAspect(property) ? property : undefined;
}

/**
 * Reads the columns out of any fee-category payload the stream carries.
 *
 * Three shapes reach this, and all three are the same statement about a category at different
 * granularity: a whole item (`CollectionItemAdded`'s value, `CollectionItemRemoved`'s expectedValue),
 * a single wrapped leaf (`CollectionItemUpdated`'s `{ "feeRate": 125 }`), and a bare scalar — which is
 * what an older event, or a caller that already unwrapped, hands over. The bare scalar needs the caller
 * to say which column it is, so it is handled by {@link readProposalFeeScheduleAspectValue} rather than
 * guessed at here.
 */
export function readProposalFeeScheduleCategoryValues(payload: unknown): ProposalFeeScheduleCategoryValues {
  if (!isRecord(payload)) return {};

  const values: ProposalFeeScheduleCategoryValues = {};
  for (const aspect of proposalFeeScheduleAspects) {
    if (Object.prototype.hasOwnProperty.call(payload, aspect)) values[aspect] = payload[aspect];
  }
  return values;
}

/**
 * One column's value out of a payload that may or may not wrap it.
 *
 * Returns `undefined` when the payload says nothing about this column, which is distinct from a payload
 * that says the column is empty (`null`) — a distinction the whole diff pipeline preserves (see
 * `proposalStrictJsonValuesEqual`) and one a revert depends on: restoring a cleared description means
 * writing `null`, not leaving it alone.
 */
export function readProposalFeeScheduleAspectValue(payload: unknown, aspect: string): unknown {
  if (isRecord(payload)) {
    return Object.prototype.hasOwnProperty.call(payload, aspect) ? payload[aspect] : undefined;
  }
  // A bare scalar is the column's own value: the caller already knows which column it asked for.
  return payload;
}

/** The path the fee schedule's categories negotiate under. */
export const proposalFeeScheduleCategoriesPath = "/feeSchedule/categories";

/**
 * A fee-category payload that addresses exactly one column, and which column that is.
 *
 * Deliberately narrow: only a single-key object on the category path, and only when that key is one of
 * the six canonical columns. A whole category (`{ id, name, feeRate, … }`) is more than one column and
 * keeps the ordinary "named by its `name`" rendering, so an add or a removal still reads as the
 * category it is about rather than as one of its columns.
 */
export function proposalFeeScheduleLeafPayload(
  path: string | null | undefined,
  value: unknown,
): { aspect: ProposalFeeScheduleAspect; value: unknown } | undefined {
  if (path !== proposalFeeScheduleCategoriesPath || !isRecord(value)) return undefined;

  const keys = Object.keys(value);
  if (keys.length !== 1 || !isProposalFeeScheduleAspect(keys[0])) return undefined;
  return { aspect: keys[0], value: value[keys[0]] };
}

/**
 * How one column reads.
 *
 * The rate is money and reads as money, which takes a currency the rate's own leaf does not carry — it
 * is a column of its own. So the category's other values are passed alongside, and a rate whose
 * currency is unknown reads as the bare number rather than being dressed in a currency nobody chose.
 */
export function proposalFeeScheduleAspectText(
  aspect: ProposalFeeScheduleAspect,
  value: unknown,
  category: ProposalFeeScheduleCategoryValues = {},
): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (aspect === "feeRate") return feeRateText(value, category.feeCurrency);
  return proposalFeeScheduleScalarText(value);
}

/** A column value that stands for itself: text, a number, a flag. Anything else reads as nothing. */
export function proposalFeeScheduleScalarText(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "string") return value.trim() || undefined;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return undefined;
}

function feeRateText(value: unknown, currency: unknown): string | undefined {
  // A rate arrives as the bare number the canonical category carries (`feeRate: 125`). An older payload
  // may still wrap it as money; read that too rather than showing nothing.
  const amount = isRecord(value) ? (value.amount ?? value.value) : value;
  if (typeof amount !== "number") return typeof amount === "string" ? amount : undefined;

  const currencyCode = typeof currency === "string" ? currency : isRecord(value) && typeof value.currency === "string" ? value.currency : undefined;
  if (!currencyCode) return String(amount);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currencyCode }).format(amount);
  } catch {
    // A change description must never throw — it is often the only thing telling a user what they are
    // about to save — so an unrecognised currency code still reads out.
    return `${amount} ${currencyCode}`;
  }
}

/**
 * The column that assigns a category to a person — the seventh property the backend's
 * `FeeCategoryEditableProperties` lists beside the six above, and the one column whose value is not its
 * own text: an `EntityMember` is two ids (`userId`, `legalEntityId`), and the person's name lives in the
 * Individuals directory. Kept apart from {@link proposalFeeScheduleAspects} because reading it takes a
 * resolver the pure rendering of the six never needs — and because the server strips it for a viewer who
 * may not see who is assigned (`ProposalFeeScheduleTeamMemberMasking`), so no id may ever be printed in
 * its place.
 */
export const proposalFeeScheduleTeamMemberProperty = "teamMember";

/** Whether `property` names the category's assigned team member. */
export function isProposalFeeScheduleTeamMemberProperty(
  property: unknown,
): property is typeof proposalFeeScheduleTeamMemberProperty {
  return property === proposalFeeScheduleTeamMemberProperty;
}

/** A column of a fee-schedule category as the stream keys it: one of the six, or the team member. */
export type ProposalFeeScheduleColumn = ProposalFeeScheduleAspect | typeof proposalFeeScheduleTeamMemberProperty;

/**
 * The column a conflict key addresses — one of the six, or the team member — if it addresses one.
 * The sibling of {@link proposalFeeScheduleAspectFromConflictKey} for a reader that names columns
 * rather than renders their values.
 */
export function proposalFeeScheduleColumnFromConflictKey(
  conflictKey?: string | null,
): ProposalFeeScheduleColumn | undefined {
  if (!conflictKey) return undefined;
  const property = conflictKey.split(":").at(-1);
  return isProposalFeeScheduleAspect(property) || isProposalFeeScheduleTeamMemberProperty(property) ? property : undefined;
}

/** How a category with nobody assigned reads — the fee schedule table's own word for it. */
export const proposalFeeScheduleUnassignedText = "Unassigned";

/** How the member reads for a viewer the server withholds it from — the fee schedule table's own word. */
export const proposalFeeScheduleRestrictedTeamMemberText = "Restricted";

/**
 * How an assigned person reads when their name is not to hand — the fee schedule table's own word for
 * that state, shown until the Individuals lookup lands. Never the id.
 */
export const proposalFeeScheduleTeamMemberFallbackText = "Team member";

/**
 * The user the team-member column holds: a user id, `null` for nobody, `undefined` when the value is not
 * a member this build can read (an older or foreign shape).
 */
export function proposalFeeScheduleTeamMemberUserId(value: unknown): string | null | undefined {
  if (value === null) return null;
  if (!isRecord(value)) return undefined;
  const userId = value.userId;
  return typeof userId === "string" && userId ? userId : undefined;
}

/**
 * How the team-member column reads: the person's name, "Unassigned" for nobody, and for a member whose
 * name cannot be resolved the table's own stand-in rather than an id (#4949 — it read "(changed)").
 *
 * `undefined` for a value that says nothing (absent) or one this build cannot read as a member at all —
 * the caller decides whether that is silence or "(changed)".
 */
export function proposalFeeScheduleTeamMemberText(
  value: unknown,
  resolveUserName?: (userId: string) => string | undefined,
): string | undefined {
  const userId = proposalFeeScheduleTeamMemberUserId(value);
  if (userId === null) return proposalFeeScheduleUnassignedText;
  if (userId === undefined) return undefined;
  return resolveUserName?.(userId)?.trim() || proposalFeeScheduleTeamMemberFallbackText;
}

/**
 * A fee-category payload that addresses exactly the team-member column — `{ "teamMember": {…} }` or
 * `{ "teamMember": null }`, the shape the command builder emits for it and the server stores — and the
 * member it carries. As narrow as {@link proposalFeeScheduleLeafPayload}, for the same reason.
 */
export function proposalFeeScheduleTeamMemberLeafPayload(
  path: string | null | undefined,
  value: unknown,
): { value: unknown } | undefined {
  if (path !== proposalFeeScheduleCategoriesPath || !isRecord(value)) return undefined;
  const keys = Object.keys(value);
  if (keys.length !== 1 || !isProposalFeeScheduleTeamMemberProperty(keys[0])) return undefined;
  return { value: value[proposalFeeScheduleTeamMemberProperty] };
}

/**
 * Every user the team-member column of a stream entry names, on either side of the change — the ids a
 * surface must look up before it can draw the names. An entry about any other column, or any other
 * collection, names nobody.
 */
export function proposalFeeScheduleTeamMemberUserIds(entry: {
  path?: string | null;
  conflictKey?: string | null;
  expectedValueExists?: boolean;
  expectedValue?: unknown;
  valueExists?: boolean;
  value?: unknown;
}): string[] {
  if (!isProposalFeeScheduleTeamMemberProperty(proposalFeeScheduleColumnFromConflictKey(entry.conflictKey))) return [];
  return [
    ...(entry.expectedValueExists ? [entry.expectedValue] : []),
    ...(entry.valueExists ? [entry.value] : []),
  ].flatMap((payload) => {
    const userId = proposalFeeScheduleTeamMemberUserId(readProposalFeeScheduleAspectValue(payload, proposalFeeScheduleTeamMemberProperty));
    return userId ? [userId] : [];
  });
}

/**
 * What a column is called, whether or not this vocabulary knows it.
 *
 * The six canonical columns keep their curated names. A seventh the stream carries and this build does
 * not know — the backend's `FeeCategoryEditableProperties` is free to grow one — is named from its own
 * property rather than dropped. A column nobody can name is a column nobody can be shown, and silence
 * about a change is the worse failure.
 */
export function proposalFeeScheduleColumnLabel(property: string): string {
  return isProposalFeeScheduleAspect(property)
    ? proposalFeeScheduleAspectLabels[property]
    : proposalCollectionItemAspectLabel(property);
}

/**
 * One column's value as text, for a column this vocabulary has no rendering rule for.
 *
 * Reads what reads — a string, a number, a flag — and otherwise says the column changed rather than
 * going quiet. Never resolves an identity or reaches for a name: a masked column arrives with its value
 * already stripped server-side (`ProposalFeeScheduleTeamMemberMasking`), and this must stay true whether
 * or not it did.
 */
export function proposalFeeScheduleUnknownColumnText(exists: boolean, value: unknown): string | undefined {
  if (!exists) return undefined;
  return proposalFeeScheduleScalarText(value) ?? proposalCollectionItemUnreadableValueText;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
